import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireAdmin } from "@/lib/admin-guard";
import {
  listWaitlistSubscribers,
  importWaitlistEmails,
  type WaitlistRow,
  type ImportEmailsResult,
} from "@/lib/waitlist-admin.functions";
import { ArrowLeft, Search, Download, Users, Upload } from "lucide-react";


const searchSchema = z.object({
  q: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  includeUnsub: z.boolean().optional(),
});

export const Route = createFileRoute("/admin_/waitlist")({
  ssr: false,
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "Waitlist — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: AdminWaitlistPage,
});

function csvEscape(v: unknown) {
  return `"${String(v ?? "").replace(/"/g, '""')}"`;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "good" | "bad" | "muted";
}) {
  const toneClass =
    tone === "good"
      ? "text-[var(--gold-deep)]"
      : tone === "bad"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="rounded-md bg-muted/30 p-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={`text-lg font-semibold ${toneClass}`}>
        {value.toLocaleString()}
      </div>
    </div>
  );
}


function AdminWaitlistPage() {
  const sp = Route.useSearch();
  const navigate = Route.useNavigate();
  const [q, setQ] = useState(sp.q ?? "");
  const [from, setFrom] = useState(sp.from ?? "");
  const [to, setTo] = useState(sp.to ?? "");
  const [includeUnsub, setIncludeUnsub] = useState(sp.includeUnsub ?? false);

  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportEmailsResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const qc = useQueryClient();
  const fetcher = useServerFn(listWaitlistSubscribers);
  const importer = useServerFn(importWaitlistEmails);
  const { data, isLoading, error } = useQuery({
    queryKey: ["admin", "waitlist", sp.q, sp.from, sp.to, sp.includeUnsub],
    queryFn: () =>
      fetcher({
        data: {
          search: sp.q || undefined,
          from: sp.from ? new Date(sp.from).toISOString() : undefined,
          to: sp.to ? new Date(sp.to + "T23:59:59").toISOString() : undefined,
          includeUnsubscribed: sp.includeUnsub ?? false,
        },
      }),
  });


  const rows: WaitlistRow[] = data?.rows ?? [];
  const total = data?.total ?? 0;

  const applyFilters = () => {
    navigate({
      search: {
        q: q.trim() || undefined,
        from: from || undefined,
        to: to || undefined,
        includeUnsub: includeUnsub || undefined,
      },
    });
  };

  const clearFilters = () => {
    setQ("");
    setFrom("");
    setTo("");
    setIncludeUnsub(false);
    navigate({ search: {} });
  };

  const stats = useMemo(() => {
    const active = rows.filter((r) => !r.unsubscribed_at).length;
    const unsub = rows.filter((r) => !!r.unsubscribed_at).length;
    return { active, unsub };
  }, [rows]);

  function exportCsv() {
    const header = [
      "created_at",
      "email",
      "consent_at",
      "consent_source",
      "unsubscribed_at",
      "platform",
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "referrer",
      "landing_path",
    ];
    const lines = rows.map((r) => [
      new Date(r.created_at).toISOString(),
      r.email,
      r.consent_at ? new Date(r.consent_at).toISOString() : "",
      r.consent_source ?? "",
      r.unsubscribed_at ? new Date(r.unsubscribed_at).toISOString() : "",
      r.platform ?? "",
      r.utm_source ?? "",
      r.utm_medium ?? "",
      r.utm_campaign ?? "",
      r.referrer ?? "",
      r.landing_path ?? "",
    ]);
    // Prepend BOM so Excel opens UTF-8 correctly.
    const csv = "\uFEFF" +
      [header, ...lines].map((r) => r.map(csvEscape).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <Link
        to="/admin"
        className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to admin
      </Link>

      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[var(--gradient-gold)] shadow-[var(--shadow-gold)]">
            <Users className="h-5 w-5 text-foreground" strokeWidth={2.25} />
          </div>
          <div>
            <h1 className="font-display text-3xl text-foreground">Waitlist</h1>
            <p className="text-sm text-muted-foreground">
              {total.toLocaleString()} total match{total === 1 ? "" : "es"}
              {" · "}
              {stats.active.toLocaleString()} active
              {stats.unsub > 0 && ` · ${stats.unsub.toLocaleString()} unsubscribed`}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              setShowImport((v) => !v);
              setImportResult(null);
              setImportError(null);
            }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-card px-4 py-2 text-sm font-semibold hover:bg-gold/10"
          >
            <Upload className="h-4 w-4" /> Import emails
          </button>
          <button
            onClick={exportCsv}
            disabled={!rows.length}
            className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-card px-4 py-2 text-sm font-semibold hover:bg-gold/10 disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
        </div>
      </header>

      {showImport && (
        <div className="mb-6 rounded-2xl border border-gold/20 bg-card p-4">
          <h2 className="mb-1 font-display text-lg text-foreground">Import waitlist emails</h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Paste one email per line (or separated by commas/spaces). Invalid
            addresses and existing subscribers are detected automatically — nothing
            is duplicated.
          </p>
          <textarea
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            rows={8}
            placeholder={"jane@example.com\njohn@example.com"}
            className="w-full rounded-lg border border-gold/30 bg-background p-3 font-mono text-xs text-foreground"
          />
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              disabled={importing || !importText.trim()}
              onClick={async () => {
                setImporting(true);
                setImportError(null);
                setImportResult(null);
                try {
                  const res = await importer({ data: { emails: importText } });
                  setImportResult(res);
                  if (res.inserted > 0) {
                    setImportText("");
                    qc.invalidateQueries({ queryKey: ["admin", "waitlist"] });
                  }
                } catch (err) {
                  setImportError((err as Error).message ?? "Import failed");
                } finally {
                  setImporting(false);
                }
              }}
              className="rounded-full bg-[var(--gradient-gold)] px-4 py-2 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)] disabled:opacity-50"
            >
              {importing ? "Importing…" : "Import"}
            </button>
            <button
              onClick={() => {
                setShowImport(false);
                setImportText("");
                setImportResult(null);
                setImportError(null);
              }}
              className="rounded-full border border-gold/30 px-4 py-2 text-sm text-foreground/80 hover:bg-gold/10"
            >
              Close
            </button>
          </div>

          {importError && (
            <p className="mt-3 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {importError}
            </p>
          )}

          {importResult && (
            <div className="mt-4 space-y-3 rounded-lg border border-gold/20 bg-background p-4 text-sm">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat label="Submitted" value={importResult.totalSubmitted} />
                <Stat label="Added" value={importResult.inserted} tone="good" />
                <Stat
                  label="Already on list"
                  value={importResult.duplicates.length}
                  tone="muted"
                />
                <Stat
                  label="Invalid format"
                  value={importResult.invalid.length}
                  tone={importResult.invalid.length ? "bad" : "muted"}
                />
              </div>
              {importResult.duplicatesInInput.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  {importResult.duplicatesInInput.length} duplicate
                  {importResult.duplicatesInInput.length === 1 ? "" : "s"} within
                  the pasted list were collapsed.
                </p>
              )}
              {importResult.invalid.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-destructive">
                    Show {importResult.invalid.length} invalid address
                    {importResult.invalid.length === 1 ? "" : "es"}
                  </summary>
                  <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-destructive/5 p-2 text-destructive">
                    {importResult.invalid.join("\n")}
                  </pre>
                </details>
              )}
              {importResult.duplicates.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground">
                    Show {importResult.duplicates.length} already on the list
                  </summary>
                  <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded bg-muted/40 p-2 text-muted-foreground">
                    {importResult.duplicates.join("\n")}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}


      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-2xl border border-gold/20 bg-card p-4">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            placeholder="Search email…"
            className="w-full rounded-full border border-gold/30 bg-background py-2 ps-9 pe-3 text-sm"
          />
        </div>
        <label className="text-xs text-muted-foreground">
          From
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="ms-2 rounded-md border border-gold/30 bg-background px-2 py-1 text-sm text-foreground"
          />
        </label>
        <label className="text-xs text-muted-foreground">
          To
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="ms-2 rounded-md border border-gold/30 bg-background px-2 py-1 text-sm text-foreground"
          />
        </label>
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={includeUnsub}
            onChange={(e) => setIncludeUnsub(e.target.checked)}
            className="h-4 w-4 accent-[var(--gold-deep)]"
          />
          Include unsubscribed
        </label>
        <button
          onClick={applyFilters}
          className="rounded-full bg-[var(--gradient-gold)] px-4 py-2 text-sm font-semibold text-foreground shadow-[var(--shadow-gold)]"
        >
          Apply
        </button>
        <button
          onClick={clearFilters}
          className="rounded-full border border-gold/30 px-4 py-2 text-sm text-foreground/80 hover:bg-gold/10"
        >
          Clear
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gold/20 bg-card">
        {error ? (
          <p className="p-6 text-sm text-destructive">
            {(error as Error).message || "Couldn't load waitlist."}
          </p>
        ) : isLoading ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            No waitlist signups match these filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gold/10 text-start text-xs uppercase tracking-wider text-foreground/70">
                <tr>
                  <th className="px-4 py-3">Joined</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Campaign</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-border/30 align-top">
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-medium text-foreground break-all">
                      {r.email}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {r.consent_source ?? r.source ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">
                      {[r.utm_source, r.utm_medium, r.utm_campaign]
                        .filter(Boolean)
                        .join(" / ") || "—"}
                    </td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">
                      {r.unsubscribed_at ? (
                        <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-destructive">
                          Unsubscribed
                        </span>
                      ) : (
                        <span className="rounded-full bg-gold-light/50 px-2 py-0.5 text-[var(--gold-deep)]">
                          Active
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-4 text-[11px] text-muted-foreground">
        Showing up to 1,000 most recent matches. Refine filters to narrow the list.
      </p>
    </div>
  );
}
